import React, { useEffect, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from 'recharts';
import { FeatherX, FeatherAward } from '@subframe/core';
import { Dialog } from '../ui/components/Dialog';
import {
  getCollectionPointsDistribution,
  type PointsDistributionResult,
} from '../api/richlist';

interface PointsDistributionModalProps {
  isOpen: boolean;
  onClose: () => void;
  collectionId: string;
  collectionName?: string;
  /**
   * Optional preloaded distribution data. If provided, the modal skips its
   * own fetch. Useful when the parent page has already loaded the data to
   * decide whether to show the trigger link.
   */
  preloaded?: PointsDistributionResult | null;
}

function formatPoints(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/\.?0+$/, '');
}

interface TooltipProps {
  active?: boolean;
  payload?: Array<{ payload: { points: number; nft_count: number; min_rank: number | null; max_rank: number | null } }>;
}

function ChartTooltip({ active, payload }: TooltipProps) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  const rankRange = d.min_rank !== null && d.max_rank !== null
    ? (d.min_rank === d.max_rank ? `Rank ${d.min_rank}` : `Ranks ${d.min_rank}–${d.max_rank}`)
    : null;
  return (
    <div className="rounded-md border border-neutral-border bg-default-background px-3 py-2 text-caption shadow-md">
      <div className="font-caption-bold text-default-font">{formatPoints(d.points)} pts</div>
      <div className="text-subtext-color">{d.nft_count.toLocaleString()} NFTs</div>
      {rankRange && <div className="text-subtext-color">{rankRange}</div>}
    </div>
  );
}

export const PointsDistributionModal: React.FC<PointsDistributionModalProps> = ({
  isOpen,
  onClose,
  collectionId,
  collectionName,
  preloaded,
}) => {
  const [data, setData] = useState<PointsDistributionResult | null>(preloaded ?? null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (preloaded) {
      setData(preloaded);
      return;
    }
    if (!isOpen || !collectionId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getCollectionPointsDistribution(collectionId)
      .then(result => { if (!cancelled) setData(result); })
      .catch(err => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, collectionId, preloaded]);

  // API returns distribution sorted by points DESC (rarest first). For the chart,
  // ascend the X-axis: lowest point tier on the left, highest on the right.
  const chartData = data
    ? [...data.distribution]
        .sort((a, b) => a.points - b.points)
        .map(d => ({ ...d, label: `${formatPoints(d.points)} pts` }))
    : [];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Content className="w-full max-w-[720px]">
        <div className="flex w-full flex-col items-start gap-4 p-6">
          <div className="flex w-full items-start justify-between">
            <div className="flex flex-col items-start gap-1">
              <div className="flex items-center gap-2">
                <FeatherAward className="h-5 w-5 text-brand-600" />
                <span className="text-heading-2 font-heading-2 text-default-font">
                  Points Distribution
                </span>
              </div>
              {collectionName && (
                <span className="text-body font-body text-subtext-color">{collectionName}</span>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded hover:bg-neutral-100"
            >
              <FeatherX className="h-5 w-5 text-subtext-color" />
            </button>
          </div>

          {loading && (
            <div className="flex h-80 w-full items-center justify-center text-subtext-color">
              Loading…
            </div>
          )}

          {!loading && error && (
            <div className="flex h-80 w-full items-center justify-center text-error-600">
              {error}
            </div>
          )}

          {!loading && !error && data && chartData.length === 0 && (
            <div className="flex h-80 w-full items-center justify-center text-subtext-color">
              No points configured for this collection yet.
            </div>
          )}

          {!loading && !error && data && chartData.length > 0 && (
            <>
              <div className="w-full">
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={chartData} margin={{ top: 20, right: 16, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                    <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(0, 177, 204, 0.08)' }} />
                    <Bar dataKey="nft_count" fill="#00b1cc" radius={[4, 4, 0, 0]}>
                      <LabelList
                        dataKey="nft_count"
                        position="top"
                        style={{ fontSize: 11, fill: '#6b7280' }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="flex w-full items-center text-caption text-subtext-color">
                <span>
                  {data.total_nfts.toLocaleString()} NFTs across {chartData.length} point tier{chartData.length === 1 ? '' : 's'}
                </span>
              </div>
            </>
          )}
        </div>
      </Dialog.Content>
    </Dialog>
  );
};
